'use client';

/**
 * Megamenu trigger + panel — ported from ThemeMaker's
 * {@code lms-portal/src/components/shell/MegaMenuLink.tsx}.
 *
 * <p>A header menu item that, on hover or keyboard focus, reveals a
 * panel of grouped links beneath it. Plain link behaviour is preserved:
 * click navigates to the link's href just like a normal menu link.
 *
 * <h3>Why position: fixed (not absolute)</h3>
 * The header may live inside a parent that has {@code overflow: hidden}
 * or {@code overflow: auto} (e.g. SplitHeader's menu row uses
 * horizontal scroll on narrow viewports). An absolutely-positioned
 * panel inside such a parent would be clipped.
 *
 * <p>Fixed-positioning escapes every containing-block constraint and
 * anchors the panel to the viewport. We track the trigger's screen
 * coords with {@code getBoundingClientRect()} and re-measure when the
 * panel opens. Side effect: the panel doesn't follow scroll, so we
 * close on scroll instead of trying to keep it pinned.
 *
 * <h3>Hover semantics</h3>
 * <ul>
 *   <li>120ms open delay so brushing across the menu doesn't pop panels</li>
 *   <li>150ms close delay so users can move into the panel without it
 *       snapping shut</li>
 *   <li>Link + panel form one shared hover region</li>
 * </ul>
 *
 * <h3>Keyboard semantics</h3>
 * <ul>
 *   <li>Focus opens the panel (after the 120ms delay)</li>
 *   <li>Tab navigates panel links; Shift+Tab back out</li>
 *   <li>Escape closes; focus restores to the trigger</li>
 * </ul>
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import type { CSSProperties } from 'react';
import type { CurrentUser, MegaMenuConfig } from '../../portal';
import { t, filterVisible } from '../../portal';
import { BlockIcon, guessSidebarIcon } from '../icons/sidebarIcons';
import { shellHref } from '../shellHref';
import { PortalLink } from '../../portal/PortalLink';

export interface MegaMenuLinkProps {
  label: string;
  href: string;
  badge?: string;
  dim?: boolean;
  /** The item's own configured icon (`item.icon`). Falls back to a
   *  label-guessed icon when unset — same rule `MenuLinkRow` applies to
   *  plain links. */
  icon?: string;
  megamenu: MegaMenuConfig;
  /** The tenant's URL slug. We rebuild URLs internally — server
   *  components can't pass functions across the server→client boundary
   *  in App Router, so we replicate the tiny tenantHref logic here. */
  tenantSlug: string;
  currentUser: CurrentUser | null;
  locale: string;
  fallbackLocale: string;
  /** Light-on-dark trigger styling for cinematic chrome. */
  inverse?: boolean;
  /** Panel layout. Defaults to {@code 'mega'}. */
  panelLayout?: 'mega' | 'flat';
  /** Whether to show the featured aside (only consulted in
   *  {@code 'mega'} layout). Computed by the caller from data presence
   *  so we don't recompute the same predicate at every layer. */
  showFeaturedPromo?: boolean;
  /** Mirrors {@code MenuLinkRow}'s `showIcon` — on by default. Must be
   *  threaded through so sibling nav triggers (e.g. a `categoriesMenu`
   *  component entry) collapse their icon box together; otherwise their
   *  trigger `<a>` heights diverge and the row's icons/labels/chevrons
   *  fall out of vertical alignment. */
  showIcon?: boolean;
}

const OPEN_DELAY_MS = 120;
const CLOSE_DELAY_MS = 150;

// Layout constants — mirrored in the CSS sizing below. Update both
// together if you tweak the grid math.
const MEGA_COL_MIN = 160; // each column's minimum content width
const MEGA_COL_GAP = 24; // gap between columns
const MEGA_FEATURED_WIDTH = 240; // featured aside width
const MEGA_FEATURED_GAP = 28; // gap between columns and featured aside
const MEGA_PANEL_PADDING_X = 24; // panel horizontal padding (each side)

/** Same resolution as the server's {@code tenantHref} — one implementation. */
function buildHref(tenantSlug: string, href: string | undefined): string {
  return shellHref(href, tenantSlug);
}

interface TriggerCoords {
  top: number;
  left: number;
  width: number;
}

export function MegaMenuLink(props: MegaMenuLinkProps) {
  const {
    label,
    href,
    badge,
    dim,
    icon,
    megamenu,
    tenantSlug,
    currentUser,
    locale,
    fallbackLocale,
    inverse,
    panelLayout = 'mega',
    showFeaturedPromo = false,
    showIcon = true,
  } = props;

  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<TriggerCoords | null>(null);
  // Portal mount gate. SSR returns null on first render (no document).
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLAnchorElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = (which: 'open' | 'close') => {
    const ref = which === 'open' ? openTimerRef : closeTimerRef;
    if (ref.current) {
      clearTimeout(ref.current);
      ref.current = null;
    }
  };

  const scheduleOpen = () => {
    cancel('close');
    if (open) return;
    cancel('open');
    openTimerRef.current = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
  };
  const scheduleClose = () => {
    cancel('open');
    cancel('close');
    closeTimerRef.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  useEffect(
    () => () => {
      cancel('open');
      cancel('close');
    },
    [],
  );

  // Measure trigger position whenever we open. useLayoutEffect so the
  // panel is positioned BEFORE the browser paints the open frame.
  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    const trig = triggerRef.current;
    if (!trig) return;

    const place = () => {
      const r = trig.getBoundingClientRect();
      const maxWidth = megamenu.maxWidth ?? 720;
      const desiredLeft = r.left;
      const overflowRight = desiredLeft + maxWidth - window.innerWidth;
      const left = overflowRight > 8 ? Math.max(8, window.innerWidth - maxWidth - 8) : desiredLeft;
      setCoords({ top: r.bottom + 4, left, width: r.width });
    };
    place();

    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open, megamenu.maxWidth]);

  // Close on scroll. Following scroll would require either re-measuring
  // every frame (jank) or pinning the panel. Closing is the cheaper
  // and more conventional behaviour.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, { passive: true, capture: true });
    return () => window.removeEventListener('scroll', close, { capture: true });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // Resolve panel content for the current user.
  const cols = filterVisible(megamenu.columns ?? [], currentUser)
    .map((c) => ({
      label: t(c.label, locale, fallbackLocale),
      links: filterVisible(c.links ?? [], currentUser),
    }))
    .filter((c) => c.links.length > 0);

  // Flat layout has no slot for the featured aside, so we force it
  // off regardless of caller intent — keeps callers from having to
  // remember the layout-vs-featured exclusivity rule.
  const showFeatured = panelLayout === 'mega' && showFeaturedPromo;

  if (cols.length === 0 && !showFeatured) {
    return (
      <PlainLink
        label={label}
        badge={badge}
        dim={dim}
        href={buildHref(tenantSlug, href)}
        inverse={inverse}
        showIcon={showIcon}
        icon={icon}
      />
    );
  }

  const maxWidth = megamenu.maxWidth ?? 720;

  return (
    <div
      ref={wrapperRef}
      onPointerEnter={scheduleOpen}
      onPointerLeave={scheduleClose}
      onFocus={scheduleOpen}
      onBlur={(e) => {
        const next = e.relatedTarget as Node | null;
        if (!wrapperRef.current?.contains(next) && !panelRef.current?.contains(next)) {
          scheduleClose();
        }
      }}
      style={{ position: 'relative', display: 'inline-flex' }}
    >
      <PortalLink
        ref={triggerRef}
        href={buildHref(tenantSlug, href)}
        aria-haspopup="true"
        aria-expanded={open}
        style={triggerStyle(dim, inverse)}
      >
        <TriggerIcon label={label} showIcon={showIcon} icon={icon} />
        <span className="lms-menu-label-text">{label}</span>
        {badge && <BadgeLabel>{badge}</BadgeLabel>}
        <ChevronDown
          size={16}
          aria-hidden
          className="lms-menu-label-text"
          style={{
            marginLeft: 4,
            opacity: 0.65,
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 120ms ease',
          }}
        />
      </PortalLink>

      {open &&
        coords &&
        mounted &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            aria-label={`${label} menu`}
            onPointerEnter={() => cancel('close')}
            onPointerLeave={scheduleClose}
            style={{
              ...panelStyle(maxWidth, showFeatured, panelLayout, cols.length),
              top: coords.top,
              left: coords.left,
            }}
          >
            <div style={panelInnerStyle(showFeatured, panelLayout)}>
              <div style={columnsStyle(cols.length, panelLayout)}>
                {cols.map((col, ci) => {
                  // Suppress column label in flat-single-column mode —
                  // the kicker just adds visual noise above an
                  // unambiguous list. Multiple columns always render
                  // the label as a group separator. Mega mode always
                  // renders the label since columns sit side-by-side.
                  const showColLabel =
                    !!col.label && !(panelLayout === 'flat' && cols.length === 1);
                  return (
                    <div
                      key={ci}
                      style={
                        panelLayout === 'flat' && cols.length > 1
                          ? { paddingTop: ci === 0 ? 0 : 8 }
                          : undefined
                      }
                    >
                      {showColLabel && <div style={columnLabelStyle}>{col.label}</div>}
                      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {col.links.map((l, li) => {
                          const labelText = t(l.label, locale, fallbackLocale);
                          // Per-link icon: explicit `icon` from config,
                          // or heuristic guess from label so menus look
                          // reasonable when authors haven't picked icons.
                          const iconName = l.icon || guessSidebarIcon(labelText);
                          const desc = l.description && t(l.description, locale, fallbackLocale);
                          return (
                            <li key={li}>
                              <PortalLink
                                href={buildHref(tenantSlug, l.href)}
                                role="menuitem"
                                className="lms-mm-link"
                                style={desc ? panelLinkStyle : panelLinkStyleCentered}
                              >
                                <span
                                  style={desc ? panelLinkIconStyle : panelLinkIconStyleCentered}
                                  aria-hidden
                                >
                                  <BlockIcon value={iconName} size={16} />
                                </span>
                                <span style={{ minWidth: 0, flex: 1 }}>
                                  <div style={panelLinkLabelStyle}>{labelText}</div>
                                  {desc && <div style={panelLinkDescStyle}>{desc}</div>}
                                </span>
                              </PortalLink>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>

              {showFeatured && megamenu.featured && (
                <FeaturedPromo
                  cfg={megamenu.featured}
                  tenantSlug={tenantSlug}
                  locale={locale}
                  fallbackLocale={fallbackLocale}
                />
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

// =============================================================================
// Sub-pieces
// =============================================================================

function PlainLink({
  label,
  badge,
  dim,
  href,
  inverse,
  showIcon,
  icon,
}: {
  label: string;
  badge?: string;
  dim?: boolean;
  href: string;
  inverse?: boolean;
  showIcon?: boolean;
  icon?: string;
}) {
  return (
    <PortalLink href={href} style={triggerStyle(dim, inverse)}>
      <TriggerIcon label={label} showIcon={showIcon} icon={icon} />
      <span className="lms-menu-label-text">{label}</span>
      {badge && <BadgeLabel>{badge}</BadgeLabel>}
    </PortalLink>
  );
}

// Trigger icon: uses the item's own configured `icon` when set (same rule
// `MenuLinkRow` applies to plain links), else guesses from the label.
// The `lms-header-nav-desktop` breakpoint (portal.css) forces this back on
// below 1023px even where a style's CSS otherwise hides menu icons — see
// `MenuLinkRow`'s `showIcon` doc comment (headers/index.tsx) for why the
// icon box must stay in the DOM (collapsed via CSS, not omitted) and why
// sibling triggers must resolve this flag the same way to stay aligned.
function TriggerIcon({
  label,
  showIcon = true,
  icon,
}: {
  label: string;
  showIcon?: boolean;
  icon?: string;
}) {
  return (
    <span
      className={showIcon ? 'lms-menu-icon' : 'lms-menu-icon lms-menu-icon--desktop-hidden'}
      aria-hidden
    >
      <BlockIcon value={icon || guessSidebarIcon(label)} size={20} />
    </span>
  );
}

function BadgeLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        background: 'var(--color-brand-500, #3b5bdb)',
        color: 'var(--color-text-on-brand, #fff)',
        fontSize: 'var(--font-size-xs, 12px)',
        fontWeight: 700,
        padding: 'var(--space-px, 1px) var(--space-1, 4px)',
        borderRadius: 'var(--radius-sm, 4px)',
        letterSpacing: 'var(--font-tracking-wider, 0.04em)',
        marginLeft: 4,
      }}
    >
      {children}
    </span>
  );
}

function FeaturedPromo({
  cfg,
  tenantSlug,
  locale,
  fallbackLocale,
}: {
  cfg: NonNullable<MegaMenuConfig['featured']>;
  tenantSlug: string;
  locale: string;
  fallbackLocale: string;
}) {
  const title = t(cfg.title, locale, fallbackLocale);
  const desc = t(cfg.description, locale, fallbackLocale);
  const ctaLabel = t(cfg.ctaLabel, locale, fallbackLocale);
  return (
    <aside
      style={{
        width: MEGA_FEATURED_WIDTH,
        flexShrink: 0,
        borderLeft: '1px solid var(--color-neutral-200, #e5e7eb)',
        paddingLeft: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        alignSelf: 'stretch',
      }}
    >
      {cfg.image && (
        <img
          src={cfg.image}
          alt={title}
          style={{
            width: '100%',
            height: 140,
            objectFit: 'cover',
            borderRadius: 'var(--radius-md, 10px)',
          }}
        />
      )}
      {title && (
        <div
          style={{
            fontWeight: 700,
            fontSize: 'var(--font-size-sm, 14px)',
            lineHeight: 1.3,
            color: 'var(--color-text-primary, #0f1218)',
          }}
        >
          {title}
        </div>
      )}
      {desc && (
        <div
          style={{
            fontSize: 'var(--font-size-xs, 12px)',
            lineHeight: 1.55,
            color: 'var(--color-text-secondary, #5f6675)',
            flex: 1,
          }}
        >
          {desc}
        </div>
      )}
      {ctaLabel && cfg.ctaHref && (
        <PortalLink
          href={buildHref(tenantSlug, cfg.ctaHref)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 'var(--font-size-xs, 12px)',
            fontWeight: 700,
            color: 'var(--color-brand-500, #3b5bdb)',
            marginTop: 'auto',
          }}
        >
          {ctaLabel}
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </PortalLink>
      )}
    </aside>
  );
}

// =============================================================================
// Styles
// =============================================================================

function triggerStyle(dim?: boolean, inverse?: boolean): CSSProperties {
  // `dim` is intentionally NOT consulted here — plain (non-dropdown)
  // menu links never dim either (see `MenuLinkRow`'s own trigger
  // style), so a megamenu trigger dimming while its plain siblings
  // stay full-strength read as one menu item being a different,
  // washed-out colour from the rest of the nav row.
  void dim;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 'var(--font-size-sm, 14px)',
    fontWeight: 500,
    color: inverse ? 'rgba(255,255,255,0.85)' : 'var(--color-text-primary, #0f1218)',
    textDecoration: 'none',
  };
}

function panelStyle(
  maxWidth: number,
  hasFeatured: boolean,
  layout: 'mega' | 'flat',
  colCount: number,
): CSSProperties {
  if (layout === 'flat') {
    return {
      position: 'fixed',
      minWidth: 220,
      maxWidth: `min(${Math.min(maxWidth, 320)}px, calc(100vw - 32px))`,
      background: 'var(--color-surface-card, #fff)',
      border: '1px solid var(--color-neutral-200, #e5e7eb)',
      borderRadius: 'var(--radius-md, 12px)',
      boxShadow: 'var(--shadow-lg, 0 12px 32px rgba(0,0,0,0.14))',
      zIndex: 1000,
      padding: 'var(--space-2, 8px) var(--space-1-5, 6px)',
      animation: 'mm-fade 80ms ease-out',
    };
  }

  // 'mega' layout: compute the minimum width content needs and use
  // that as both the floor and the target width. Content-driven sizing
  // avoids the overflow bug where a 720px cap couldn't accommodate
  // 3 columns + featured aside + gaps + padding (~876px required).
  const requiredCols = colCount * MEGA_COL_MIN + Math.max(0, colCount - 1) * MEGA_COL_GAP;
  const requiredFeatured = hasFeatured ? MEGA_FEATURED_GAP + MEGA_FEATURED_WIDTH : 0;
  const requiredWidth = requiredCols + requiredFeatured + MEGA_PANEL_PADDING_X * 2;

  // Target = max(tenant intent, content requirement). Tenant maxWidth
  // is no longer a hard ceiling — content expands past it when needed.
  const target = Math.max(maxWidth, requiredWidth);
  const widthExpr = `min(${target}px, calc(100vw - 32px))`;

  return {
    position: 'fixed',
    minWidth: Math.min(requiredWidth, 360),
    maxWidth: widthExpr,
    width: widthExpr,
    background: 'var(--color-surface-card, #fff)',
    border: '1px solid var(--color-neutral-200, #e5e7eb)',
    borderRadius: 'var(--radius-md, 14px)',
    boxShadow: 'var(--shadow-xl, 0 24px 48px rgba(0,0,0,0.18))',
    zIndex: 1000,
    padding: `20px ${MEGA_PANEL_PADDING_X}px`,
    animation: 'mm-fade 80ms ease-out',
    // Safety net: between 768px (mobile drawer kicks in below this)
    // and the content's required width, the panel can be narrower
    // than grid + aside need. overflow: hidden trims the column edge
    // instead of letting the grid overlap the aside.
    overflow: 'hidden',
  };
}

function panelInnerStyle(hasFeatured: boolean, layout: 'mega' | 'flat'): CSSProperties {
  if (layout === 'flat') {
    return {
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
      alignItems: 'stretch',
    };
  }
  return {
    display: 'flex',
    gap: hasFeatured ? MEGA_FEATURED_GAP : 0,
    alignItems: 'stretch',
    flexDirection: hasFeatured ? 'row' : 'column',
  };
}

function columnsStyle(count: number, layout: 'mega' | 'flat'): CSSProperties {
  if (layout === 'flat') {
    return {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minWidth: 0,
    };
  }
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(${Math.max(count, 1)}, minmax(${MEGA_COL_MIN}px, 1fr))`,
    gap: MEGA_COL_GAP,
    flex: 1,
    minWidth: 0,
  };
}

const columnLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs, 12px)',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 'var(--font-tracking-widest, 0.1em)',
  color: 'var(--color-text-secondary, #5f6675)',
  marginBottom: 12,
  paddingLeft: 30, // align with link labels (icon 22px + 8px gap)
  fontFamily: 'var(--font-family-mono, ui-monospace, monospace)',
};

const panelLinkStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: 'var(--space-2, 8px) var(--space-2-5, 10px)',
  textDecoration: 'none',
  color: 'inherit',
  borderRadius: 8,
  // Hover styles applied via `.lms-mm-link` global CSS class
  // (inline :hover isn't supported).
};

// Description-less links are a single line, so the icon should sit on
// the label's center line (matches CategoriesMenuComponent's row style)
// rather than top-aligned — `flex-start` above exists only to keep the
// icon pinned to the label's first line when a second description line
// follows it.
const panelLinkStyleCentered: CSSProperties = {
  ...panelLinkStyle,
  alignItems: 'center',
};

const panelLinkIconStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 22,
  height: 22,
  flexShrink: 0,
  marginTop: 1,
  color: 'var(--color-brand-500, #3b5bdb)',
};

const panelLinkIconStyleCentered: CSSProperties = {
  ...panelLinkIconStyle,
  marginTop: 0,
};

const panelLinkLabelStyle: CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: 1.3,
  color: 'var(--color-text-primary, #0f1218)',
};

const panelLinkDescStyle: CSSProperties = {
  fontSize: '11px',
  lineHeight: 1.45,
  color: 'var(--color-text-secondary, #5f6675)',
  marginTop: 3,
};
